/**
 * CapTableCacheService
 *
 * In-memory cache for cap table data with Web Worker-based LZString
 * compression/decompression to keep heavy work off the main thread.
 *
 * Usage:
 *   CapTableCacheService.init()                     — create worker (call once at startup)
 *   CapTableCacheService.get(client)                — sync read from cache (null if not loaded)
 *   CapTableCacheService.set(client, data, compress) — cache + async compress to sessionStorage
 *   CapTableCacheService.loadFromSessionStorage(client) — async decompress from sessionStorage
 *   CapTableCacheService.clear(client)              — remove from cache
 */

// LZString source embedded for the inline Web Worker (no external file needed).
// This is the same IIFE from src/shared_assets/lzstring.js (~4KB minified).
const LZSTRING_SOURCE = `
let LZString=(function(){let r=String.fromCharCode,o="ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=",n="ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+-$",e={};function t(r,o){if(!e[r]){e[r]={};for(let n=0;n<r.length;n++)e[r][r.charAt(n)]=n}return e[r][o]}const i={compressToBase64:function(r){if(null==r)return"";let n=i._compress(r,6,function(r){return o.charAt(r)});switch(n.length%4){default:case 0:return n;case 1:return n+"===";case 2:return n+"==";case 3:return n+"="}},decompressFromBase64:function(r){return null==r?"":(""==r?null:i._decompress(r.length,32,function(n){return t(o,r.charAt(n))}))},compressToUTF16:function(o){return null==o?"":i._compress(o,15,function(o){return r(o+32)})+" "},decompressFromUTF16:function(r){return null==r?"":(""==r?null:i._decompress(r.length,16384,function(o){return r.charCodeAt(o)-32}))},compressToUint8Array:function(r){const o=i.compress(r),n=new Uint8Array(2*o.length),t=o.length;for(let e=0;e<t;e++){let s=o.charCodeAt(e);n[2*e]=s>>>8;n[2*e+1]=s%256}return n},decompressFromUint8Array:function(o){if(null==o)return i.decompress(o);const n=new Array(o.length/2),t=n.length;for(let e=0;e<t;e++)n[e]=256*o[2*e]+o[2*e+1];let s=[];return n.forEach(function(o){s.push(r(o))}),i.decompress(s.join(""))},compressToEncodedURIComponent:function(r){return null==r?"":i._compress(r,6,function(r){return n.charAt(r)})},decompressFromEncodedURIComponent:function(r){return null==r?"":(""==r?null:(r=r.replace(/ /g,"+"),i._decompress(r.length,32,function(o){return t(n,r.charAt(o))})))},compress:function(o){return i._compress(o,16,function(o){return r(o)})},_compress:function(r,o,n){if(null==r)return"";let e,t,i,s={},u={},a="",p="",c="",l=2,f=3,h=2,d=[],m=0,v=0;for(i=0;i<r.length;i+=1)if(a=r.charAt(i),Object.prototype.hasOwnProperty.call(s,a)||(s[a]=f++,u[a]=!0),p=c+a,Object.prototype.hasOwnProperty.call(s,p))c=p;else{if(Object.prototype.hasOwnProperty.call(u,c)){if(c.charCodeAt(0)<256){for(e=0;e<h;e++)m<<=1,v==o-1?(v=0,d.push(n(m)),m=0):v++;for(t=c.charCodeAt(0),e=0;e<8;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1}else{for(t=1,e=0;e<h;e++)m=(m<<1)|t,v==o-1?(v=0,d.push(n(m)),m=0):v++,t=0;for(t=c.charCodeAt(0),e=0;e<16;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1}0==--l&&(l=Math.pow(2,h),h++),delete u[c]}else for(t=s[c],e=0;e<h;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1;0==--l&&(l=Math.pow(2,h),h++),s[p]=f++,c=String(a)}if(""!==c){if(Object.prototype.hasOwnProperty.call(u,c)){if(c.charCodeAt(0)<256){for(e=0;e<h;e++)m<<=1,v==o-1?(v=0,d.push(n(m)),m=0):v++;for(t=c.charCodeAt(0),e=0;e<8;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1}else{for(t=1,e=0;e<h;e++)m=(m<<1)|t,v==o-1?(v=0,d.push(n(m)),m=0):v++,t=0;for(t=c.charCodeAt(0),e=0;e<16;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1}0==--l&&(l=Math.pow(2,h),h++),delete u[c]}else for(t=s[c],e=0;e<h;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1;0==--l&&(l=Math.pow(2,h),h++)}for(t=2,e=0;e<h;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1;for(;;){if(m<<=1,v==o-1){d.push(n(m));break}v++}return d.join("")},decompress:function(r){return null==r?"":(""==r?null:i._decompress(r.length,32768,function(o){return r.charCodeAt(o)}))},_decompress:function(o,n,e){let t,i,s,u,a,p,c,l=[],f=4,h=4,d=3,m="",v=[],g={val:e(0),position:n,index:1};for(t=0;t<3;t+=1)l[t]=t;for(s=0,a=Math.pow(2,2),p=1;p!=a;)u=g.val&g.position,g.position>>=1,0==g.position&&(g.position=n,g.val=e(g.index++)),s|=(u>0?1:0)*p,p<<=1;switch(s){case 0:for(s=0,a=Math.pow(2,8),p=1;p!=a;)u=g.val&g.position,g.position>>=1,0==g.position&&(g.position=n,g.val=e(g.index++)),s|=(u>0?1:0)*p,p<<=1;c=r(s);break;case 1:for(s=0,a=Math.pow(2,16),p=1;p!=a;)u=g.val&g.position,g.position>>=1,0==g.position&&(g.position=n,g.val=e(g.index++)),s|=(u>0?1:0)*p,p<<=1;c=r(s);break;case 2:return""}for(l[3]=c,i=c,v.push(c);;){if(g.index>o)return"";for(s=0,a=Math.pow(2,d),p=1;p!=a;)u=g.val&g.position,g.position>>=1,0==g.position&&(g.position=n,g.val=e(g.index++)),s|=(u>0?1:0)*p,p<<=1;switch(c=s){case 0:for(s=0,a=Math.pow(2,8),p=1;p!=a;)u=g.val&g.position,g.position>>=1,0==g.position&&(g.position=n,g.val=e(g.index++)),s|=(u>0?1:0)*p,p<<=1;l[h++]=r(s),c=h-1,f--;break;case 1:for(s=0,a=Math.pow(2,16),p=1;p!=a;)u=g.val&g.position,g.position>>=1,0==g.position&&(g.position=n,g.val=e(g.index++)),s|=(u>0?1:0)*p,p<<=1;l[h++]=r(s),c=h-1,f--;break;case 2:return v.join("")}if(0==f&&(f=Math.pow(2,d),d++),l[c])m=l[c];else{if(c!==h)return null;m=i+i.charAt(0)}v.push(m),l[h++]=i+m.charAt(0),i=m,0==--f&&(f=Math.pow(2,d),d++)}}}; return i})();
`;

const WORKER_CODE = `
${LZSTRING_SOURCE}
self.onmessage = function(e) {
	const { type, key, data, id } = e.data;
	let result;
	if (type === 'compress') {
		result = LZString.compress(data);
	} else if (type === 'decompress') {
		result = LZString.decompress(data);
	}
	self.postMessage({ type: type + 'd', key: key, data: result, id: id });
};
`;

const CapTableCacheService = {
	_cache: {},
	_worker: null,
	_pendingCallbacks: {},
	_nextId: 0,

	init() {
		try {
			const blob = new Blob([WORKER_CODE], { type: 'application/javascript' });
			this._worker = new Worker(URL.createObjectURL(blob));
			this._worker.onmessage = (e) => {
				const { id, data } = e.data;
				const cb = this._pendingCallbacks[id];
				if (cb) {
					delete this._pendingCallbacks[id];
					cb(data);
				}
			};
			this._worker.onerror = (err) => {
				console.error('[CapTableCache] Worker error:', err);
			};
			console.log('[Performance] LZString Web Worker initialized');
		} catch (err) {
			console.warn('[CapTableCache] Worker creation failed, using synchronous fallback:', err);
			this._worker = null;
		}
	},

	/**
	 * Synchronous read from in-memory cache.
	 * Returns the cached array or null if not yet loaded.
	 */
	get(client) {
		return this._cache[client] || null;
	},

	/**
	 * Set in-memory cache and compress to sessionStorage off-thread.
	 * @param {string} client
	 * @param {Array} data - parsed cap table array
	 * @param {boolean} compress - whether this client needs compression
	 */
	set(client, data, compress) {
		this._cache[client] = data;
		const json = JSON.stringify(data);

		if (compress && this._worker) {
			const id = this._nextId++;
			this._worker.postMessage({ type: 'compress', key: client + '.CapTable', data: json, id: id });
			this._pendingCallbacks[id] = (compressed) => {
				try {
					sessionStorage.setItem(client + '.CapTable', compressed);
				} catch (e) {
					console.error('[CapTableCache] sessionStorage write failed:', e);
				}
			};
		} else if (compress) {
			// Synchronous fallback if worker unavailable
			sessionStorage.setItem(client + '.CapTable', LZString.compress(json));
		} else {
			sessionStorage.setItem(client + '.CapTable', json);
		}
	},

	/**
	 * Async load from sessionStorage with worker decompression.
	 * Used on cold page load when cache is empty but sessionStorage has data.
	 * @param {string} client
	 * @returns {Promise<Array>}
	 */
	loadFromSessionStorage(client) {
		return new Promise((resolve) => {
			const raw = sessionStorage.getItem(client + '.CapTable');
			if (!raw) {
				resolve([]);
				return;
			}

			if (this._worker) {
				const id = this._nextId++;
				this._worker.postMessage({ type: 'decompress', key: client + '.CapTable', data: raw, id: id });
				this._pendingCallbacks[id] = (decompressed) => {
					try {
						const json = decompressed || raw;
						const parsed = JSON.parse(json);
						if (Array.isArray(parsed)) {
							this._cache[client] = parsed;
							resolve(parsed);
						} else {
							console.error('[CapTableCache] Data is not an array for client ' + client);
							resolve([]);
						}
					} catch (e) {
						// Decompression returned garbage — try raw (uncompressed data)
						try {
							const parsed = JSON.parse(raw);
							if (Array.isArray(parsed)) {
								this._cache[client] = parsed;
								resolve(parsed);
							} else {
								resolve([]);
							}
						} catch (e2) {
							console.error('[CapTableCache] JSON parse failed for client ' + client + ':', e2);
							resolve([]);
						}
					}
				};
			} else {
				// Synchronous fallback
				try {
					const decompressed = LZString.decompress(raw);
					const json = decompressed || raw;
					const parsed = JSON.parse(json);
					if (Array.isArray(parsed)) {
						this._cache[client] = parsed;
						resolve(parsed);
					} else {
						resolve([]);
					}
				} catch (e) {
					try {
						const parsed = JSON.parse(raw);
						if (Array.isArray(parsed)) {
							this._cache[client] = parsed;
							resolve(parsed);
						} else {
							resolve([]);
						}
					} catch (e2) {
						console.error('[CapTableCache] Fallback parse failed for client ' + client + ':', e2);
						resolve([]);
					}
				}
			}
		});
	},

	clear(client) {
		delete this._cache[client];
	},
};

export { CapTableCacheService };
